import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Clock,
  FolderKanban,
  Users,
  Users2,
  Building2,
  CheckSquare,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Palmtree,
  Calculator,
  Settings,
  Car,
  Receipt,
  FileSpreadsheet,
  CalendarDays,
  History,
  CalendarCheck,
  Wallet,
  Timer,
  CalendarRange,
  Briefcase,
  ClipboardCheck,
  CreditCard,
  Shield,
  Tag,
  FolderTree,
  Sun,
  Moon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { NotificationBell } from './NotificationBell';
import { WeekSubmissionReminder } from './WeekSubmissionReminder';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

// CTI Logo - local asset
const CTI_LOGO = "/cti-logo.png";

// Nav items with original structure (path, label, icon, roles)
// Note: For area-controlled items (datev, vacation-accounts, audit), roles include supervisor
// but actual visibility is controlled by area_permissions loaded from backend
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/time-tracking', label: 'Zeiterfassung', icon: Clock, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/mileage', label: 'Kilometer', icon: Car, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/time-account', label: 'Mein Arbeitszeitkonto', icon: Calculator, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/leave', label: 'Abwesenheiten', icon: Palmtree, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/calendar', label: 'Betriebskalender', icon: CalendarDays, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/holidays', label: 'Feiertage', icon: CalendarCheck, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/vacation-accounts', label: 'Urlaubskonten', icon: Wallet, roles: ['accounting', 'admin', 'supervisor'], areaKey: 'vacation_accounts' },
  { path: '/projects', label: 'Aufträge', icon: FolderKanban, roles: ['employee', 'supervisor', 'accounting', 'admin'] },
  { path: '/customers', label: 'Kunden', icon: Building2, roles: ['supervisor', 'accounting', 'admin'] },
  { path: '/team-overview', label: 'Team-Übersicht', icon: Users2, roles: ['supervisor', 'accounting', 'admin'] },
  { path: '/team/time-account', label: 'Arbeitszeitkonten', icon: Wallet, roles: ['accounting', 'admin', 'supervisor'] },
  { path: '/approvals', label: 'Genehmigungen', icon: CheckSquare, roles: ['supervisor', 'admin'] },
  { path: '/reports', label: 'Berichte', icon: BarChart3, roles: ['supervisor', 'accounting', 'admin'] },
  { path: '/billing', label: 'Abrechnung', icon: Receipt, roles: ['accounting', 'admin', 'supervisor'] },
  { path: '/datev', label: 'DATEV Export', icon: FileSpreadsheet, roles: ['accounting', 'admin', 'supervisor'], areaKey: 'payroll' },
  { path: '/audit', label: 'Änderungsprotokoll', icon: History, roles: ['accounting', 'admin', 'supervisor'], areaKey: 'audit_log' },
  { path: '/users', label: 'Benutzer', icon: Users, roles: ['admin'] },
  { path: '/settings', label: 'Einstellungen', icon: Settings, roles: ['admin'] },
  { path: '/service-categories', label: 'Leistungskategorien', icon: Tag, roles: ['admin'] },
  { path: '/category-report', label: 'Leistungsauswertung', icon: FolderTree, roles: ['supervisor', 'accounting', 'admin'] },
];

// Navigation groups with their items (references to paths)
const navGroups = [
  {
    id: 'meine-zeit',
    title: 'Meine Zeit',
    icon: Timer,
    items: ['/time-tracking', '/mileage', '/time-account', '/leave'],
  },
  {
    id: 'planung',
    title: 'Planung',
    icon: CalendarRange,
    items: ['/calendar', '/holidays', '/vacation-accounts'],
  },
  {
    id: 'projekte-kunden',
    title: 'Projekte & Kunden',
    icon: Briefcase,
    items: ['/projects', '/customers'],
  },
  {
    id: 'freigaben-auswertung',
    title: 'Freigaben & Berichte',
    icon: ClipboardCheck,
    items: ['/team-overview', '/team/time-account', '/approvals', '/reports', '/category-report'],
  },
  {
    id: 'abrechnung',
    title: 'Abrechnung',
    icon: CreditCard,
    items: ['/billing', '/datev'],
  },
  {
    id: 'verwaltung',
    title: 'Verwaltung',
    icon: Shield,
    items: ['/audit', '/users', '/settings', '/service-categories'],
  },
];

// Helper to get nav item by path
const getNavItemByPath = (path) => navItems.find((item) => item.path === path);

// Helper to find which group contains a path
const findGroupByPath = (path) => navGroups.find((group) => group.items.includes(path));

export const Layout = ({ children }) => {
  const { user, api, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [accountingCanSeeTeamOverview, setAccountingCanSeeTeamOverview] = useState(false);
  const [areaPermissions, setAreaPermissions] = useState({
    payroll: ['accounting', 'supervisor'],
    unlock: ['accounting', 'supervisor'],
    overtime_ledger: ['accounting', 'supervisor'],
    vacation_accounts: ['accounting', 'supervisor'],
    audit_log: [],
  });
  
  // Track expanded groups - initialize with "meine-zeit" always open
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = { 'meine-zeit': true };
    return initial;
  });

  // Fetch area permissions (once on load)
  useEffect(() => {
    api.get('/company-settings/area-permissions')
      .then(res => {
        if (res.data) {
          setAreaPermissions(res.data);
        }
      })
      .catch(() => {
        // Keep defaults on error
      });
  }, [api]);

  // Fetch team overview settings for accounting role
  useEffect(() => {
    if (user?.role === 'accounting') {
      api.get('/company-settings/team-overview')
        .then(res => {
          setAccountingCanSeeTeamOverview(res.data?.accounting_sees_team_overview || false);
        })
        .catch(() => {
          setAccountingCanSeeTeamOverview(false);
        });
    }
  }, [api, user?.role]);

  // Fetch pending approvals count for supervisor/admin
  const fetchPendingApprovals = useCallback(async () => {
    if (user?.role === 'supervisor' || user?.role === 'admin') {
      try {
        const res = await api.get('/approvals/pending/count');
        setPendingApprovalsCount(res.data?.count || 0);
      } catch (err) {
        console.error('Failed to fetch pending approvals:', err);
      }
    }
  }, [api, user?.role]);

  // Initial fetch, polling every 60 seconds, and listen for approval changes
  useEffect(() => {
    fetchPendingApprovals();
    const interval = setInterval(fetchPendingApprovals, 60000);
    
    // Listen for approval changes from ApprovalsPage
    const handleApprovalsChanged = () => fetchPendingApprovals();
    window.addEventListener('ctime:approvals-changed', handleApprovalsChanged);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('ctime:approvals-changed', handleApprovalsChanged);
    };
  }, [fetchPendingApprovals]);

  // Auto-expand group if there are pending approvals
  useEffect(() => {
    if (pendingApprovalsCount > 0 && !expandedGroups['freigaben-auswertung']) {
      setExpandedGroups((prev) => ({ ...prev, 'freigaben-auswertung': true }));
    }
  }, [pendingApprovalsCount, expandedGroups]);

  // Get the group that contains the current path
  const activeGroup = useMemo(() => findGroupByPath(location.pathname), [location.pathname]);

  // Auto-expand the group containing the active page
  useEffect(() => {
    if (activeGroup && !expandedGroups[activeGroup.id]) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroup.id]: true }));
    }
  }, [activeGroup, expandedGroups]);

  // Filter groups and their items based on user role and area permissions
  const filteredGroups = useMemo(() => {
    // Helper function to check area permission
    const hasAreaPermission = (areaKey) => {
      if (!areaKey) return true; // No area key means always visible
      if (user?.role === 'admin') return true; // Admin always has access
      const allowedRoles = areaPermissions[areaKey] || [];
      return allowedRoles.includes(user?.role);
    };
    
    return navGroups
      .map((group) => {
        const visibleItems = group.items
          .map(getNavItemByPath)
          .filter((item) => {
            if (!item || !item.roles.includes(user?.role)) return false;
            // Special case: accounting can only see team-overview if setting is enabled
            if (item.path === '/team-overview' && user?.role === 'accounting' && !accountingCanSeeTeamOverview) {
              return false;
            }
            // Check area permissions for items with areaKey
            if (item.areaKey && !hasAreaPermission(item.areaKey)) {
              return false;
            }
            return true;
          });
        return { ...group, visibleItems };
      })
      .filter((group) => group.visibleItems.length > 0);
  }, [user?.role, accountingCanSeeTeamOverview, areaPermissions]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      supervisor: 'Vorgesetzter',
      accounting: 'Buchhaltung',
      employee: 'Mitarbeiter',
    };
    return labels[role] || role;
  };

  // Render a single nav item (with optional badge for approvals)
  const renderNavItem = (item, isActive, onClick = null, indent = true) => {
    const Icon = item.icon;
    const showBadge = item.path === '/approvals' && pendingApprovalsCount > 0;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`nav-${item.path.slice(1)}`}
        onClick={onClick}
        className={`sidebar-link ${indent ? 'sidebar-link-sub' : ''} ${isActive ? 'active' : ''}`}
      >
        <Icon className="w-4 h-4" />
        <span className="text-sm flex-1">{item.label}</span>
        {showBadge && (
          <Badge 
            variant="destructive" 
            className="ml-auto h-5 min-w-[20px] px-1.5 text-xs bg-amber-500 hover:bg-amber-500 text-white"
          >
            {pendingApprovalsCount}
          </Badge>
        )}
      </Link>
    );
  };

  // Render grouped navigation for desktop
  const renderDesktopNav = () => (
    <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
      {getNavItemByPath('/dashboard') && (
        <div className="mb-3 pb-3 border-b border-border">
          {renderNavItem(getNavItemByPath('/dashboard'), location.pathname === '/dashboard', null, false)}
        </div>
      )}
      {filteredGroups.map((group) => {
        const isExpanded = expandedGroups[group.id];
        const GroupIcon = group.icon;
        const hasActiveItem = group.visibleItems.some(
          (item) => location.pathname === item.path
        );
        // Show indicator on group if it contains approvals and has pending items
        const hasPendingApprovals = group.id === 'freigaben-auswertung' && pendingApprovalsCount > 0;

        return (
          <div key={group.id} className="mb-1">
            {/* Group header */}
            <button
              onClick={() => toggleGroup(group.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left leading-tight text-xs font-medium uppercase tracking-wider transition-colors ${
                hasActiveItem
                  ? 'text-indigo-400 bg-indigo-500/10'
                  : hasPendingApprovals && !isExpanded
                  ? 'text-amber-400 bg-amber-500/10'
                  : 'text-muted-foreground hover:text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="flex items-center gap-2">
                <GroupIcon className="w-4 h-4" />
                <span>{group.title}</span>
                {/* Badge indicator on collapsed group */}
                {hasPendingApprovals && !isExpanded && (
                  <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                )}
              </div>
              <div className="flex items-center gap-1">
                {hasPendingApprovals && !isExpanded && (
                  <Badge 
                    variant="destructive" 
                    className="h-4 min-w-[16px] px-1 text-[10px] bg-amber-500 hover:bg-amber-500 text-white"
                  >
                    {pendingApprovalsCount}
                  </Badge>
                )}
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </div>
            </button>

            {/* Group items */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-1 space-y-0.5 ml-5 border-l border-border">
                    {group.visibleItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return renderNavItem(item, isActive);
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </nav>
  );

  // Render grouped navigation for mobile
  const renderMobileNav = () => (
    <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-8rem)]">
      {getNavItemByPath('/dashboard') && (
        <div className="mb-3 pb-3 border-b border-border">
          {renderNavItem(getNavItemByPath('/dashboard'), location.pathname === '/dashboard', () => setMobileMenuOpen(false), false)}
        </div>
      )}
      {filteredGroups.map((group) => {
        const isExpanded = expandedGroups[group.id];
        const GroupIcon = group.icon;
        const hasActiveItem = group.visibleItems.some(
          (item) => location.pathname === item.path
        );
        const hasPendingApprovals = group.id === 'freigaben-auswertung' && pendingApprovalsCount > 0;

        return (
          <div key={group.id} className="mb-1">
            {/* Group header */}
            <button
              onClick={() => toggleGroup(group.id)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left leading-tight text-xs font-medium uppercase tracking-wider transition-colors ${
                hasActiveItem
                  ? 'text-indigo-400 bg-indigo-500/10'
                  : hasPendingApprovals && !isExpanded
                  ? 'text-amber-400 bg-amber-500/10'
                  : 'text-muted-foreground hover:text-muted-foreground hover:bg-accent'
              }`}
            >
              <div className="flex items-center gap-2">
                <GroupIcon className="w-4 h-4" />
                <span>{group.title}</span>
                {hasPendingApprovals && !isExpanded && (
                  <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                )}
              </div>
              <div className="flex items-center gap-1">
                {hasPendingApprovals && !isExpanded && (
                  <Badge 
                    variant="destructive" 
                    className="h-4 min-w-[16px] px-1 text-[10px] bg-amber-500 hover:bg-amber-500 text-white"
                  >
                    {pendingApprovalsCount}
                  </Badge>
                )}
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </div>
            </button>

            {/* Group items */}
            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-1 space-y-0.5 ml-5 border-l border-border">
                    {group.visibleItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      return renderNavItem(item, isActive, () => setMobileMenuOpen(false));
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
      
      {/* Logout button for mobile */}
      <div className="pt-4 border-t border-border mt-4">
        <button
          onClick={handleLogout}
          className="w-full sidebar-link text-red-600 dark:text-red-400 hover:text-red-300"
        >
          <LogOut className="w-5 h-5" />
          <span>Abmelden</span>
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-sidebar-background border-r border-sidebar-border">
        <div className="flex flex-col flex-1 min-h-0 pt-6">
          {/* Logo */}
          <div className="px-6 mb-6">
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-3">
              <img 
                src={CTI_LOGO} 
                alt="CTI" 
                className="w-8 h-8 object-contain"
              />
              <span>CTIme App</span>
            </h1>
          </div>

          {/* Grouped Navigation */}
          {renderDesktopNav()}

          {/* Theme Toggle, Notification Bell & User Menu */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9 rounded-lg hover:bg-accent"
                title={theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Moon className="h-5 w-5 text-slate-600" />
                )}
              </Button>
              <NotificationBell />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <Avatar className="w-9 h-9 bg-indigo-500/20 border border-indigo-500/30">
                    <AvatarFallback className="bg-transparent text-indigo-600 dark:text-indigo-400 text-sm font-medium">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-muted" />
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={handleLogout}
                  className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <h1 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <img 
              src={CTI_LOGO} 
              alt="CTI" 
              className="w-6 h-6 object-contain"
            />
            <span>CTIme App</span>
          </h1>
          <div className="flex items-center gap-2">
            {/* Theme Toggle for mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9 rounded-lg"
              title={theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              ) : (
                <Moon className="h-5 w-5 text-slate-600" />
              )}
            </Button>
            <NotificationBell />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-accent"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden fixed inset-0 z-30 bg-background pt-16"
          >
            {renderMobileNav()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Week Submission Reminder Dialog - global, shows once per app start */}
      {user && <WeekSubmissionReminder />}

      {/* Main Content */}
      <main className="flex-1 md:pl-64">
        <div className="pt-16 md:pt-0 min-h-screen">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="p-4 md:p-8"
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
};
